import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
    // Agent's own inquiries
    myInquiries: [],
    myInquiriesPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    // Admin's view of all inquiries
    allInquiries: [],
    allInquiriesPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    selectedInquiry: null,
    // Packages available for an agent to pick from when creating an inquiry
    agentPackages: [],
    selectedAgentPackage: null,
    inquiryLogs: [],
    loading: false,
    saving: false,
};

// --- Agent packages (for the New Inquiry form) ---
export const fetchAgentPackagesAsync = createAsyncThunk(
    "crmInquiry/fetchAgentPackages",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.agentPackages, {
                params: { status: "Active", limit: 100, ...params },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchAgentPackageByIdAsync = createAsyncThunk(
    "crmInquiry/fetchAgentPackageById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.agentPackages}/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Agent inquiries ---
export const fetchMyInquiriesAsync = createAsyncThunk(
    "crmInquiry/fetchMine",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.agentInquiries, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchMyInquiryByIdAsync = createAsyncThunk(
    "crmInquiry/fetchMineById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.agentInquiries}/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const createInquiryAsync = createAsyncThunk(
    "crmInquiry/create",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await crmAxios.post(crmService.agentInquiries, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const updateInquiryAsync = createAsyncThunk(
    "crmInquiry/update",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(`${crmService.agentInquiries}/${id}`, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const setInquiryPassengersAsync = createAsyncThunk(
    "crmInquiry/setPassengers",
    async ({ id, passengers }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(
                `${crmService.agentInquiries}/${id}/passengers`,
                { passengers }
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const setInquiryHotelsAsync = createAsyncThunk(
    "crmInquiry/setHotels",
    async ({ id, selections }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(`${crmService.agentInquiries}/${id}/hotels`, {
                selections,
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchInquiryLogsAdminAsync = createAsyncThunk(
    "crmInquiry/fetchLogsAdmin",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.adminInquiries}/${id}/logs`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchInquiryLogsMineAsync = createAsyncThunk(
    "crmInquiry/fetchLogsMine",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.agentInquiries}/${id}/logs`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Quotations ---
export const saveQuotationAsync = createAsyncThunk(
    "crmInquiry/saveQuotation",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.put(
                `${crmService.adminInquiries}/${id}/quotation`,
                payload
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const sendQuotationAsync = createAsyncThunk(
    "crmInquiry/sendQuotation",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.post(
                `${crmService.adminInquiries}/${id}/quotation/send`
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const respondQuotationAsync = createAsyncThunk(
    "crmInquiry/respondQuotation",
    async ({ id, action, rejectionReason }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.post(
                `${crmService.agentInquiries}/${id}/quotation/respond`,
                { action, rejectionReason }
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

// --- Admin inquiries ---
export const fetchAllInquiriesAsync = createAsyncThunk(
    "crmInquiry/fetchAll",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.adminInquiries, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchInquiryByIdAdminAsync = createAsyncThunk(
    "crmInquiry/fetchByIdAdmin",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.adminInquiries}/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const setInquiryStatusAsync = createAsyncThunk(
    "crmInquiry/setStatus",
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.patch(`${crmService.adminInquiries}/${id}/status`, {
                status,
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

const crmInquirySlice = createSlice({
    name: "crmInquiry",
    initialState,
    reducers: {
        clearSelectedInquiry: (state) => {
            state.selectedInquiry = null;
            state.inquiryLogs = [];
        },
        clearSelectedAgentPackage: (state) => {
            state.selectedAgentPackage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Agent packages
            .addCase(fetchAgentPackagesAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.agentPackages = payload.data.packages;
            })
            .addCase(fetchAgentPackageByIdAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedAgentPackage = payload.data;
            })
            // My inquiries
            .addCase(fetchMyInquiriesAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchMyInquiriesAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.myInquiries = payload.data.inquiries;
                    state.myInquiriesPagination = payload.data.pagination;
                }
            })
            .addCase(fetchMyInquiriesAsync.rejected, (state, { payload }) => {
                state.loading = false;
                errorToast(payload?.message || "Failed to load inquiries");
            })
            .addCase(fetchMyInquiryByIdAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedInquiry = payload.data;
            })
            .addCase(createInquiryAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(createInquiryAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                } else {
                    errorToast(payload?.message || "Failed to create inquiry");
                }
            })
            .addCase(createInquiryAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to create inquiry");
            })
            .addCase(updateInquiryAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(updateInquiryAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                } else {
                    errorToast(payload?.message || "Update failed");
                }
            })
            .addCase(updateInquiryAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Update failed");
            })
            .addCase(setInquiryPassengersAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedInquiry = payload.data;
                else errorToast(payload?.message || "Failed to save passengers");
            })
            .addCase(setInquiryPassengersAsync.rejected, (state, { payload }) => {
                errorToast(payload?.message || "Failed to save passengers");
            })
            .addCase(setInquiryHotelsAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedInquiry = payload.data;
                else errorToast(payload?.message || "Failed to save hotel selection");
            })
            .addCase(setInquiryHotelsAsync.rejected, (state, { payload }) => {
                errorToast(payload?.message || "Failed to save hotel selection");
            })
            // Quotations
            .addCase(saveQuotationAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(saveQuotationAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                } else {
                    errorToast(payload?.message || "Failed to save quotation");
                }
            })
            .addCase(saveQuotationAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to save quotation");
            })
            .addCase(sendQuotationAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(sendQuotationAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                } else {
                    errorToast(payload?.message || "Failed to send quotation");
                }
            })
            .addCase(sendQuotationAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to send quotation");
            })
            .addCase(respondQuotationAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(respondQuotationAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                } else {
                    errorToast(payload?.message || "Failed to respond to quotation");
                }
            })
            .addCase(respondQuotationAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to respond to quotation");
            })
            // Admin inquiries
            .addCase(fetchAllInquiriesAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllInquiriesAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.allInquiries = payload.data.inquiries;
                    state.allInquiriesPagination = payload.data.pagination;
                }
            })
            .addCase(fetchAllInquiriesAsync.rejected, (state, { payload }) => {
                state.loading = false;
                errorToast(payload?.message || "Failed to load inquiries");
            })
            .addCase(fetchInquiryByIdAdminAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.selectedInquiry = payload.data;
            })
            .addCase(setInquiryStatusAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(setInquiryStatusAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.selectedInquiry = payload.data;
                    state.allInquiries = state.allInquiries.map((i) =>
                        i.id === payload.data.id ? payload.data : i
                    );
                } else {
                    errorToast(payload?.message || "Failed to update status");
                }
            })
            .addCase(setInquiryStatusAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Failed to update status");
            })
            // Inquiry activity log
            .addCase(fetchInquiryLogsAdminAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.inquiryLogs = payload.data;
            })
            .addCase(fetchInquiryLogsMineAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.inquiryLogs = payload.data;
            });
    },
});

export const { clearSelectedInquiry, clearSelectedAgentPackage } =
    crmInquirySlice.actions;

export const selectMyInquiries = (state) => state.crmInquiry.myInquiries;
export const selectMyInquiriesPagination = (state) =>
    state.crmInquiry.myInquiriesPagination;
export const selectAllInquiries = (state) => state.crmInquiry.allInquiries;
export const selectAllInquiriesPagination = (state) =>
    state.crmInquiry.allInquiriesPagination;
export const selectSelectedInquiry = (state) => state.crmInquiry.selectedInquiry;
export const selectInquiryLogs = (state) => state.crmInquiry.inquiryLogs;
export const selectAgentPackages = (state) => state.crmInquiry.agentPackages;
export const selectSelectedAgentPackage = (state) =>
    state.crmInquiry.selectedAgentPackage;
export const selectInquiryLoading = (state) => state.crmInquiry.loading;
export const selectInquirySaving = (state) => state.crmInquiry.saving;

export default crmInquirySlice.reducer;
